import { ref, computed, watch, onUnmounted, unref, type Ref, type MaybeRef } from 'vue';
import api from '@/services/api';
import { formatApiErrorMessage, formatUserFacingError } from '@/utils/formatUserFacingError';
import { CERT_EXPIRED_CRAWL_MESSAGE, hasNewCertExpiredFailure } from '@/utils/certExpiredCrawl';
import {
  getLtcsDuplicatesEndpoint,
  LTCS_TYPE_IDX_KEY,
} from '@/constants/ltcsMacro';
import { getLtcsResultHeaders } from '@/constants/ltcsResultHeaders';
import type { LtcsDuplicateGroup } from '@/components/member/request/LtcsDuplicateDialog.vue';

export const LTCS_TYPE_LABELS: Record<
  string,
  { name: string; color: string; icon: string; description: string; needsYearMonth: boolean }
> = {
  caion_ltcs_recipient_info: {
    name: '수급자 정보',
    description: '롱텀케어에서 수급자 정보를 가져옵니다.',
    icon: 'mdi-account-heart',
    color: 'secondary',
    needsYearMonth: false,
  },
  caion_ltcs_emp_info: {
    name: '요양보호사 정보',
    description: '롱텀케어에서 요양보호사 정보를 가져옵니다.',
    icon: 'mdi-badge-account',
    color: 'success',
    needsYearMonth: false,
  },
  caion_ltcs_schedule_plan: {
    name: '요양보호사 일정계획',
    description: '롱텀케어에서 요양보호사 일정계획을 가져옵니다.',
    icon: 'mdi-calendar-clock',
    color: 'info',
    needsYearMonth: true,
  },
  caion_ltcs_rfid: {
    name: 'RFID',
    description: '롱텀케어에서 RFID 전송내용을 가져옵니다.',
    icon: 'mdi-nfc-variant',
    color: 'info',
    needsYearMonth: true,
  },
  caion_ltcs_claim: {
    name: '청구내역',
    description: '롱텀케어에서 청구내역을 가져옵니다.',
    icon: 'mdi-file-document-outline',
    color: 'primary',
    needsYearMonth: true,
  },
  caion_ltcs_claim_detail: {
    name: '청구내역상세',
    description: '롱텀케어에서 청구내역 상세를 가져옵니다.',
    icon: 'mdi-file-document-multiple',
    color: 'secondary',
    needsYearMonth: true,
  },
  caion_ltcs_labor_cost: {
    name: '인건비지출내역',
    description: '롱텀케어에서 종사자인건비 지출내역을 가져옵니다.',
    icon: 'mdi-cash-multiple',
    color: 'success',
    needsYearMonth: true,
  },
};

export const HR_LTCS_REQUEST_TYPES = [
  'caion_ltcs_recipient_info',
  'caion_ltcs_emp_info',
  'caion_ltcs_schedule_plan',
  'caion_ltcs_rfid',
] as const;

/** 임금설정(방문요양·목욕) — 청구·인건비 자료요청 */
export const WAGE_LTCS_REQUEST_TYPES = [
  'caion_ltcs_claim',
  'caion_ltcs_claim_detail',
  'caion_ltcs_labor_cost',
] as const;

/** 임금설정 빈 화면 — 청구내역만 */
export const WAGE_CLAIM_LTCS_REQUEST_TYPES = [
  'caion_ltcs_claim',
  'caion_ltcs_claim_detail',
] as const;

export const normalizeLtcsType = (type: string) => {
  if (type === 'caion_ltcs_caregiver_plan') return 'caion_ltcs_schedule_plan';
  if (type === 'caion_ltcs_caregiver_info') return 'caion_ltcs_emp_info';
  return type;
};

const getEndpointConfig = (type: string) => {
  const normalized = normalizeLtcsType(type);
  const config: Record<string, { endpoint: string; defaultSortKey: string; needsYearMonth: boolean }> = {
    caion_ltcs_recipient_info: { endpoint: '/ltcs/recipient-info', defaultSortKey: 'clsr_recipient_name', needsYearMonth: false },
    caion_ltcs_emp_info: { endpoint: '/ltcs/caregiver-info', defaultSortKey: 'clci_name', needsYearMonth: false },
    caion_ltcs_schedule_plan: { endpoint: '/ltcs/caregiver-plan', defaultSortKey: 'clsp_date', needsYearMonth: true },
    caion_ltcs_rfid: { endpoint: '/ltcs/rfid', defaultSortKey: 'clrf_start_dt', needsYearMonth: true },
    caion_ltcs_claim: { endpoint: '/ltcs/claim', defaultSortKey: 'clc_date', needsYearMonth: true },
    caion_ltcs_claim_detail: { endpoint: '/ltcs/claim-detail', defaultSortKey: 'clcd_date', needsYearMonth: true },
    caion_ltcs_labor_cost: { endpoint: '/ltcs/labor-cost', defaultSortKey: 'yearMonth', needsYearMonth: true },
  };
  return config[normalized] || { endpoint: '/ltcs/recipient-info', defaultSortKey: 'clsr_recipient_name', needsYearMonth: false };
};

interface UseLtcsMacroRequestOptions {
  memberCode: Ref<string>;
  bizGubun: Ref<string>;
  selectedYearMonth: Ref<string>;
  allowedTypes?: readonly string[];
  /** true(관리자): 원문 에러, false(고객): 간단한 안내 */
  verboseErrors?: MaybeRef<boolean>;
}

export function useLtcsMacroRequest(options: UseLtcsMacroRequestOptions) {
  const { memberCode, bizGubun, selectedYearMonth, allowedTypes, verboseErrors } = options;

  const isVerbose = () => unref(verboseErrors) === true;

  const historyLoading = ref(false);
  const historyItems = ref<any[]>([]);
  const loadingMap = ref<Record<string, boolean>>({});
  let pollingInterval: ReturnType<typeof setInterval> | null = null;

  const snackbar = ref({ show: false, message: '', color: 'success' });

  const resultModal = ref({
    show: false,
    status: '' as 'completed' | 'failed' | '',
    type: '',
    requestedAt: '',
    completedAt: '',
    result: null as any,
    errorMessage: '',
    yearMonth: '',
    bizGubun: '',
  });

  const scheduleLoading = ref(false);
  const scheduleSearch = ref('');
  const scheduleData = ref({
    items: [] as any[],
    total: 0,
    summary: {
      totalCount: 0,
      caregiverCount: 0,
      gradeCount: 0,
      totalFee: 0,
      recipientCount: 0,
      dateCount: 0,
      duplicateCount: 0,
      duplicateGroupCount: 0,
      portalRawCount: 0,
      paidCount: 0,
      requestedCount: 0,
      statusPairCount: 0,
    },
  });

  const duplicateDialogShow = ref(false);
  const duplicateLoading = ref(false);
  const duplicateGroups = ref<LtcsDuplicateGroup[]>([]);
  const duplicateCount = computed(
    () => Number(scheduleData.value.summary.duplicateCount) || 0
  );
  const duplicateGroupCount = computed(
    () => Number(scheduleData.value.summary.duplicateGroupCount) || 0
  );
  const duplicateIdxKey = computed(() => {
    const type = normalizeLtcsType(resultModal.value.type);
    return LTCS_TYPE_IDX_KEY[type] || 'idx';
  });

  const currentPage = ref(1);
  const itemsPerPage = ref(50);
  const sortBy = ref([{ key: 'clsp_date', order: 'asc' as const }]);
  const totalPages = computed(() => Math.ceil(scheduleData.value.total / itemsPerPage.value));

  const requestTypes = computed(() => {
    const types = allowedTypes?.length
      ? allowedTypes
      : Object.keys(LTCS_TYPE_LABELS);
    return types
      .filter((type) => LTCS_TYPE_LABELS[type])
      .map((type) => ({ type, ...LTCS_TYPE_LABELS[type] }));
  });

  const filteredHistoryItems = computed(() => {
    if (!allowedTypes?.length) return historyItems.value;
    return historyItems.value.filter((item) =>
      allowedTypes.includes(normalizeLtcsType(item.cmq_type))
    );
  });

  const hasActiveRequest = computed(() =>
    filteredHistoryItems.value.some(
      (item) => item.cmq_status === 'pending' || item.cmq_status === 'processing'
    )
  );

  const scheduleHeaders = computed(() =>
    getLtcsResultHeaders(normalizeLtcsType(resultModal.value.type))
  );

  const showCaregiverCount = computed(() => {
    const types = ['caion_ltcs_emp_info', 'caion_ltcs_schedule_plan', 'caion_ltcs_claim', 'caion_ltcs_claim_detail'];
    return types.includes(resultModal.value.type);
  });

  const showRecipientCount = computed(() => {
    const types = ['caion_ltcs_recipient_info', 'caion_ltcs_schedule_plan', 'caion_ltcs_claim', 'caion_ltcs_claim_detail'];
    return types.includes(resultModal.value.type);
  });

  const showDateCount = computed(() => {
    const types = ['caion_ltcs_schedule_plan', 'caion_ltcs_claim'];
    return types.includes(resultModal.value.type);
  });

  const getTypeLabel = (type: string) => LTCS_TYPE_LABELS[normalizeLtcsType(type)]?.name || type;
  const getTypeColor = (type: string) => LTCS_TYPE_LABELS[normalizeLtcsType(type)]?.color || 'grey';
  const needsYearMonth = (type: string) => LTCS_TYPE_LABELS[normalizeLtcsType(type)]?.needsYearMonth ?? true;

  const getLatestProgressMessage = (item: any) => {
    const progress = item.cmq_progress;
    if (!progress) return '';
    try {
      const list = Array.isArray(progress) ? progress : typeof progress === 'string' ? JSON.parse(progress) : [];
      const last = list[list.length - 1];
      return last?.message || last?.status || '';
    } catch {
      return '';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return '대기중';
      case 'processing':
        return '처리중';
      case 'completed':
        return '완료';
      case 'failed':
        return '실패';
      case 'cancelled':
        return '취소됨';
      default:
        return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'processing':
        return 'info';
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'cancelled':
        return 'grey';
      default:
        return 'grey';
    }
  };

  const getParamsLabel = (params: any) => {
    if (!params) return '-';
    try {
      const p = typeof params === 'string' ? JSON.parse(params) : params;
      const ym = String(p.yearMonth || '');
      const biz = p.bizGubun || '';
      if (!ym && !biz) return '연월 없음';
      if (ym.length === 6) {
        const label = `${ym.substring(0, 4)}년 ${parseInt(ym.substring(4, 6), 10)}월 (${ym})`;
        return biz ? `${label} / ${biz}` : label;
      }
      return biz ? `${ym} / ${biz}` : ym;
    } catch {
      return '-';
    }
  };

  const formatDateTime = (date: string) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('ko-KR');
  };

  const formatNumber = (num: number | undefined | null) => {
    if (num === undefined || num === null) return '0';
    return num.toLocaleString('ko-KR');
  };

  const formatYearMonth = (ym: string) => {
    if (!ym || ym.length !== 6) return ym || '';
    const year = ym.substring(0, 4);
    const month = ym.substring(4, 6);
    return `${year}년 ${parseInt(month)}월`;
  };

  const formatDateFull = (date: string) => {
    if (!date) return '-';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatTimeRange = (start: string, end: string) => {
    if (!start || !end) return '-';
    return `${start.substring(0, 5)} ~ ${end.substring(0, 5)}`;
  };

  const formatTime = (time: string) => {
    if (!time) return '-';
    return time.substring(0, 5);
  };

  const formatMoney = (val: number | string | null | undefined) => {
    if (val === null || val === undefined || val === '') return '-';
    const n = Number(val);
    if (Number.isNaN(n)) return String(val);
    return `${n.toLocaleString()}원`;
  };

  const formatRate = (val: number | string | null | undefined) => {
    if (val === null || val === undefined || val === '') return '-';
    const n = Number(val);
    if (Number.isNaN(n)) return String(val);
    return `${n}%`;
  };

  const formatYn = (val: string | null | undefined) => {
    if (!val || val === 'N') return '-';
    if (val === 'Y') return 'Y';
    return val;
  };

  const getServiceTypeColor = (type: string) => {
    switch (type) {
      case '방문요양':
        return 'success';
      case '방문목욕':
        return 'info';
      case '방문간호':
        return 'error';
      case '주야간보호':
        return 'warning';
      default:
        return 'grey';
    }
  };

  const getClaimStatusColor = (status: string) => {
    switch (status) {
      case '지급':
      case '승인':
        return 'success';
      case '반려':
        return 'error';
      case '심사중':
        return 'warning';
      case '청구의뢰':
      case '청구완료':
        return 'info';
      default:
        return 'grey';
    }
  };

  const showMessage = (message: string, color = 'success') => {
    snackbar.value = { show: true, message, color };
  };

  const formatFailureMessage = (message: string, fallback = '요청 처리에 실패했습니다.') =>
    formatUserFacingError(message, { verbose: isVerbose(), fallback });

  const formatProgressMessage = (message: string) => {
    if (!message?.trim()) return '';
    if (isVerbose()) return message;
    return formatUserFacingError(message, {
      fallback: '자료를 가져오는 중입니다.',
    });
  };

  const getLatestRequestForType = (type: string, yearMonth?: string) => {
    const normalized = normalizeLtcsType(type);
    return filteredHistoryItems.value.find((item) => {
      if (normalizeLtcsType(item.cmq_type) !== normalized) return false;
      if (!yearMonth) return true;
      try {
        const p = typeof item.cmq_params === 'string' ? JSON.parse(item.cmq_params) : item.cmq_params;
        return p?.yearMonth === yearMonth;
      } catch {
        return false;
      }
    });
  };

  const confirmDialog = ref({
    show: false,
    type: '',
    typeName: '',
    yearMonth: '',
    bizGubun: '',
    needsYearMonth: true,
  });

  /** 요청 전 확인 다이얼로그 오픈 */
  const submitRequest = async (type: string) => {
    if (needsYearMonth(type)) {
      if (!selectedYearMonth.value) {
        showMessage('조회 연월을 선택해주세요.', 'error');
        return;
      }
      if (!bizGubun.value) {
        showMessage('사업구분이 선택되지 않았습니다.', 'error');
        return;
      }
    }
    if (!memberCode.value) {
      showMessage(
        isVerbose() ? '기관 정보(m_code)를 불러올 수 없습니다.' : '기관 정보를 불러올 수 없습니다.',
        'error'
      );
      return;
    }

    confirmDialog.value = {
      show: true,
      type,
      typeName: getTypeLabel(type),
      yearMonth: needsYearMonth(type) ? selectedYearMonth.value : '',
      bizGubun: needsYearMonth(type) ? bizGubun.value : '',
      needsYearMonth: needsYearMonth(type),
    };
  };

  /** 확인 후 실제 API 전송 */
  const confirmSubmitRequest = async () => {
    const type = confirmDialog.value.type;
    if (!type) return;

    loadingMap.value[type] = true;
    try {
      const params: Record<string, string> = { m_code: memberCode.value };
      if (needsYearMonth(type)) {
        params.yearMonth = selectedYearMonth.value;
        params.bizGubun = bizGubun.value;
      }

      await api.post('/macro/request', {
        m_code: memberCode.value,
        cmq_type: type,
        params,
      });
      confirmDialog.value.show = false;
      const ymHint = needsYearMonth(type)
        ? ` (${formatYearMonth(selectedYearMonth.value)} · ${selectedYearMonth.value})`
        : '';
      showMessage(`${getTypeLabel(type)} 요청이 등록되었습니다.${ymHint}`);
      await loadHistory();
      startPolling();
    } catch (error: any) {
      showMessage(
        formatApiErrorMessage(error, {
          verbose: isVerbose(),
          fallback: '요청 등록에 실패했습니다.',
        }),
        'error'
      );
    } finally {
      loadingMap.value[type] = false;
    }
  };

  const cancelRequest = async (item: any) => {
    try {
      await api.post(`/macro/${item.cmq_idx}/cancel`);
      showMessage('요청이 취소되었습니다.');
      await loadHistory();
    } catch (error: any) {
      showMessage(
        formatApiErrorMessage(error, {
          verbose: isVerbose(),
          fallback: '요청 취소에 실패했습니다.',
        }),
        'error'
      );
    }
  };

  const loadScheduleData = async () => {
    if (!memberCode.value) return;

    const { endpoint, defaultSortKey, needsYearMonth: typeNeedsYm } = getEndpointConfig(resultModal.value.type);
    const modalBizGubun = resultModal.value.bizGubun || bizGubun.value;
    if (typeNeedsYm && (!modalBizGubun || !resultModal.value.yearMonth)) return;

    scheduleLoading.value = true;
    try {
      if (resultModal.value.type === 'caion_ltcs_labor_cost') {
        try {
          const res = await api.get(`${endpoint}/${memberCode.value}/${resultModal.value.yearMonth}`, {
            params: { bizGubun: modalBizGubun },
          });
          if (res.data.data) {
            scheduleData.value.items = [res.data.data];
            scheduleData.value.total = 1;
            scheduleData.value.summary = {
              totalCount: 1,
              caregiverCount: 0,
              gradeCount: 0,
              totalFee: (res.data.data.totalCost || 0) + (res.data.data.totalAdd || 0),
              recipientCount: 0,
              dateCount: 0,
              duplicateCount: 0,
              duplicateGroupCount: 0,
            };
            await loadLaborCostDuplicateStats(modalBizGubun);
          } else {
            scheduleData.value.items = [];
            scheduleData.value.total = 0;
            scheduleData.value.summary = {
              totalCount: 0,
              caregiverCount: 0,
              gradeCount: 0,
              totalFee: 0,
              recipientCount: 0,
              dateCount: 0,
              duplicateCount: 0,
              duplicateGroupCount: 0,
            };
          }
        } catch (error) {
          console.error('인건비지출내역 조회 실패:', error);
          scheduleData.value.items = [];
          scheduleData.value.total = 0;
        }
        return;
      }

      const sortKey = sortBy.value[0]?.key || defaultSortKey;
      const sortOrder = sortBy.value[0]?.order || 'asc';
      const queryParams: Record<string, any> = {
        mCode: memberCode.value,
        search: scheduleSearch.value,
        sortBy: sortKey,
        sortOrder,
        limit: itemsPerPage.value,
        offset: (currentPage.value - 1) * itemsPerPage.value,
      };
      if (modalBizGubun) queryParams.bizGubun = modalBizGubun;
      if (resultModal.value.yearMonth) queryParams.yearMonth = resultModal.value.yearMonth;

      const res = await api.get(endpoint, { params: queryParams });
      scheduleData.value.items = res.data.data || [];
      scheduleData.value.total = res.data.total || 0;

      if (!scheduleSearch.value) {
        const summaryRes = await api.get(`${endpoint}/summary`, {
          params: {
            mCode: memberCode.value,
            ...(modalBizGubun ? { bizGubun: modalBizGubun } : {}),
            ...(resultModal.value.yearMonth ? { yearMonth: resultModal.value.yearMonth } : {}),
          },
        });
        scheduleData.value.summary = summaryRes.data.data || {};
      }
    } catch (error) {
      console.error('데이터 조회 실패:', error);
      showMessage('데이터 조회에 실패했습니다.', 'error');
    } finally {
      scheduleLoading.value = false;
    }
  };

  const loadLaborCostDuplicateStats = async (modalBizGubun: string) => {
    const endpoint = getLtcsDuplicatesEndpoint('caion_ltcs_labor_cost');
    if (!endpoint || !memberCode.value) return;
    try {
      const res = await api.get(endpoint, {
        params: {
          mCode: memberCode.value,
          bizGubun: modalBizGubun,
          yearMonth: resultModal.value.yearMonth,
        },
      });
      const data = res.data?.data || {};
      scheduleData.value.summary.duplicateCount = data.duplicateCount || 0;
      scheduleData.value.summary.duplicateGroupCount = data.duplicateGroupCount || 0;
    } catch (error) {
      console.error('인건비 중복 통계 조회 실패:', error);
    }
  };

  const loadDuplicateGroups = async () => {
    const endpoint = getLtcsDuplicatesEndpoint(resultModal.value.type);
    if (!endpoint || !memberCode.value) return;

    const modalBizGubun = resultModal.value.bizGubun || bizGubun.value;
    duplicateLoading.value = true;
    try {
      const res = await api.get(endpoint, {
        params: {
          mCode: memberCode.value,
          ...(modalBizGubun ? { bizGubun: modalBizGubun } : {}),
          ...(resultModal.value.yearMonth ? { yearMonth: resultModal.value.yearMonth } : {}),
          groupLimit: 50,
        },
      });
      const data = res.data?.data || {};
      duplicateGroups.value = data.groups || [];
      if (typeof data.duplicateCount === 'number') {
        scheduleData.value.summary.duplicateCount = data.duplicateCount;
      }
      if (typeof data.duplicateGroupCount === 'number') {
        scheduleData.value.summary.duplicateGroupCount = data.duplicateGroupCount;
      }
    } catch (error: any) {
      console.error('중복 데이터 조회 실패:', error);
      showMessage(
        formatApiErrorMessage(error, {
          verbose: isVerbose(),
          fallback: '중복 데이터 조회에 실패했습니다.',
        }),
        'error'
      );
    } finally {
      duplicateLoading.value = false;
    }
  };

  const openDuplicateDialog = async () => {
    if (duplicateCount.value <= 0) return;
    duplicateDialogShow.value = true;
    duplicateGroups.value = [];
    await loadDuplicateGroups();
  };

  const showResultModal = async (item: any) => {
    let yearMonth = '';
    let reqBizGubun = '';
    try {
      const params = typeof item.cmq_params === 'string' ? JSON.parse(item.cmq_params) : item.cmq_params;
      yearMonth = params?.yearMonth || '';
      reqBizGubun = params?.bizGubun || '';
    } catch {
      yearMonth = '';
      reqBizGubun = '';
    }

    const normalizedType = normalizeLtcsType(item.cmq_type);
    resultModal.value = {
      show: true,
      status: item.cmq_status,
      type: normalizedType,
      requestedAt: item.cmq_requested_at,
      completedAt: item.cmq_completed_at,
      result: item.cmq_result,
      errorMessage: formatFailureMessage(item.cmq_error_message || '', '요청 처리에 실패했습니다.'),
      yearMonth,
      bizGubun: reqBizGubun,
    };

    const supportedTypes = Object.keys(LTCS_TYPE_LABELS);
    if (item.cmq_status === 'completed' && supportedTypes.includes(normalizedType)) {
      currentPage.value = 1;
      scheduleSearch.value = '';
      const { defaultSortKey } = getEndpointConfig(normalizedType);
      sortBy.value = [{ key: defaultSortKey, order: 'asc' as const }];
      await loadScheduleData();
    }
  };

  const closeResultModal = () => {
    resultModal.value.show = false;
    duplicateDialogShow.value = false;
    duplicateGroups.value = [];
    scheduleData.value = {
      items: [],
      total: 0,
      summary: {
        totalCount: 0,
        caregiverCount: 0,
        gradeCount: 0,
        totalFee: 0,
        recipientCount: 0,
        dateCount: 0,
        duplicateCount: 0,
        duplicateGroupCount: 0,
        portalRawCount: 0,
        paidCount: 0,
        requestedCount: 0,
        statusPairCount: 0,
      },
    };
    scheduleSearch.value = '';
    currentPage.value = 1;
  };

  const searchScheduleData = () => {
    currentPage.value = 1;
    loadScheduleData();
  };

  const clearSearch = () => {
    scheduleSearch.value = '';
    currentPage.value = 1;
    loadScheduleData();
  };

  const onSortChange = (newSort: any) => {
    sortBy.value = newSort;
    loadScheduleData();
  };

  const onPageChange = () => {
    loadScheduleData();
  };

  const onItemsPerPageChange = () => {
    currentPage.value = 1;
    loadScheduleData();
  };

  const loadHistory = async () => {
    if (!memberCode.value) return;
    historyLoading.value = true;
    try {
      const res = await api.get(`/macro/member/${memberCode.value}`, { params: { limit: 50 } });
      const next = res.data.data || [];
      if (hasNewCertExpiredFailure(historyItems.value, next)) {
        showMessage(CERT_EXPIRED_CRAWL_MESSAGE, 'error');
      }
      historyItems.value = next;
    } catch (error) {
      console.error('요청 내역 조회 실패:', error);
    } finally {
      historyLoading.value = false;
    }
  };

  const startPolling = () => {
    if (pollingInterval) return;
    pollingInterval = setInterval(() => {
      if (hasActiveRequest.value) {
        loadHistory();
      } else {
        stopPolling();
      }
    }, 3000);
  };

  const stopPolling = () => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      pollingInterval = null;
    }
  };

  watch(hasActiveRequest, (hasActive) => {
    if (hasActive) startPolling();
    else stopPolling();
  });

  watch(
    () => memberCode.value,
    (code) => {
      if (code) loadHistory();
    },
    { immediate: true }
  );

  onUnmounted(() => {
    stopPolling();
  });

  return {
    requestTypes,
    filteredHistoryItems,
    historyLoading,
    loadingMap,
    hasActiveRequest,
    snackbar,
    resultModal,
    scheduleLoading,
    scheduleSearch,
    scheduleData,
    duplicateDialogShow,
    duplicateLoading,
    duplicateGroups,
    duplicateCount,
    duplicateGroupCount,
    duplicateIdxKey,
    currentPage,
    itemsPerPage,
    sortBy,
    totalPages,
    scheduleHeaders,
    showCaregiverCount,
    showRecipientCount,
    showDateCount,
    getTypeLabel,
    getTypeColor,
    getLatestProgressMessage,
    getStatusLabel,
    getStatusColor,
    getParamsLabel,
    getLatestRequestForType,
    formatDateTime,
    formatNumber,
    formatYearMonth,
    formatDateFull,
    formatTimeRange,
    formatTime,
    formatMoney,
    formatRate,
    formatYn,
    getServiceTypeColor,
    getClaimStatusColor,
    confirmDialog,
    submitRequest,
    confirmSubmitRequest,
    cancelRequest,
    showResultModal,
    closeResultModal,
    searchScheduleData,
    clearSearch,
    onSortChange,
    onPageChange,
    onItemsPerPageChange,
    loadHistory,
    loadScheduleData,
    openDuplicateDialog,
    formatFailureMessage,
    formatProgressMessage,
    showMessage,
  };
}

export type LtcsRequestBtnStatus = 'empty' | 'ready' | 'processing' | 'failed';

/** 리모컨 버튼 — 타입별 짧은 라벨 */
export const LTCS_TYPE_SHORT_LABELS: Record<string, string> = {
  caion_ltcs_recipient_info: '수급자',
  caion_ltcs_emp_info: '종사자',
  caion_ltcs_schedule_plan: '일정',
  caion_ltcs_rfid: 'RFID',
  caion_ltcs_claim: '청구',
  caion_ltcs_claim_detail: '청구상세',
  caion_ltcs_labor_cost: '지출내역',
};

export interface LtcsTypeStatusItem {
  type: string;
  label: string;
  status: LtcsRequestBtnStatus;
}

const getShortLabel = (type: string) => {
  const normalized = normalizeLtcsType(type);
  return LTCS_TYPE_SHORT_LABELS[normalized] || LTCS_TYPE_LABELS[normalized]?.name || type;
};

const findLatestMacroForType = (
  history: any[],
  type: string,
  yearMonth?: string,
  bizGubun?: string
) => {
  const normalized = normalizeLtcsType(type);
  return history.find((item) => {
    if (normalizeLtcsType(item.cmq_type) !== normalized) return false;
    try {
      const p = typeof item.cmq_params === 'string' ? JSON.parse(item.cmq_params) : item.cmq_params;
      if (yearMonth && p?.yearMonth !== yearMonth) return false;
      if (bizGubun && p?.bizGubun && p.bizGubun !== bizGubun) return false;
      return true;
    } catch {
      return false;
    }
  });
};

const resolveTypeStatus = (
  latest: any | undefined,
  dataCount: number
): LtcsRequestBtnStatus => {
  if (latest && (latest.cmq_status === 'pending' || latest.cmq_status === 'processing')) {
    return 'processing';
  }
  if (dataCount > 0) return 'ready';
  if (latest?.cmq_status === 'failed') return 'failed';
  return 'empty';
};

const fetchTypeDataCount = async (
  type: string,
  memberCode: string,
  bizGubun: string,
  yearMonth: string
): Promise<number> => {
  const normalized = normalizeLtcsType(type);
  const params: Record<string, string> = { mCode: memberCode };
  if (bizGubun) params.bizGubun = bizGubun;
  if (yearMonth) params.yearMonth = yearMonth;

  switch (normalized) {
    case 'caion_ltcs_recipient_info': {
      const res = await api.get('/ltcs/recipient-info/summary', { params: { mCode: memberCode } });
      return res.data?.data?.totalCount || 0;
    }
    case 'caion_ltcs_emp_info': {
      const res = await api.get('/ltcs/caregiver-info/summary', { params: { mCode: memberCode } });
      return res.data?.data?.totalCount || 0;
    }
    case 'caion_ltcs_schedule_plan': {
      const res = await api.get('/ltcs/caregiver-plan/summary', { params });
      return res.data?.data?.totalCount || 0;
    }
    case 'caion_ltcs_rfid': {
      const res = await api.get('/ltcs/rfid/summary', { params });
      return res.data?.data?.totalCount || 0;
    }
    case 'caion_ltcs_claim': {
      const res = await api.get('/ltcs/claim/summary', { params });
      return res.data?.data?.totalCount || 0;
    }
    case 'caion_ltcs_claim_detail': {
      const res = await api.get('/ltcs/claim-detail/summary', { params });
      return res.data?.data?.totalCount || 0;
    }
    case 'caion_ltcs_labor_cost': {
      if (!yearMonth || !bizGubun) return 0;
      try {
        const res = await api.get(`/ltcs/labor-cost/${memberCode}/${yearMonth}`, {
          params: { bizGubun },
        });
        return res.data?.data ? 1 : 0;
      } catch {
        return 0;
      }
    }
    default:
      return 0;
  }
};

export const aggregateLtcsBtnStatus = (
  items: LtcsTypeStatusItem[]
): LtcsRequestBtnStatus => {
  const statuses = items.map((item) => item.status);
  if (statuses.some((s) => s === 'processing')) return 'processing';
  if (statuses.length > 0 && statuses.every((s) => s === 'ready')) return 'ready';
  if (statuses.some((s) => s === 'failed')) return 'failed';
  return 'empty';
};

/** 리모컨 버튼 — 타입별 수집 상태 (한 줄 표시용) */
export async function fetchLtcsTypeStatuses(
  memberCode: string,
  bizGubun: string,
  yearMonth: string,
  types: readonly string[]
): Promise<LtcsTypeStatusItem[]> {
  if (!memberCode) {
    return types.map((type) => ({
      type,
      label: getShortLabel(type),
      status: 'empty' as LtcsRequestBtnStatus,
    }));
  }

  try {
    const historyRes = await api.get(`/macro/member/${memberCode}`, { params: { limit: 50 } });
    const history = historyRes.data?.data || [];

    return Promise.all(
      types.map(async (type) => {
        const normalized = normalizeLtcsType(type);
        const label = getShortLabel(type);
        const needsYm = LTCS_TYPE_LABELS[normalized]?.needsYearMonth ?? false;
        const ym = needsYm ? yearMonth : undefined;
        const biz = needsYm ? bizGubun : undefined;

        if (needsYm && (!yearMonth || !bizGubun)) {
          return { type: normalized, label, status: 'empty' as LtcsRequestBtnStatus };
        }

        const latest = findLatestMacroForType(history, type, ym, biz);
        const count = await fetchTypeDataCount(normalized, memberCode, bizGubun, yearMonth);
        return {
          type: normalized,
          label,
          status: resolveTypeStatus(latest, count),
        };
      })
    );
  } catch {
    return types.map((type) => ({
      type: normalizeLtcsType(type),
      label: getShortLabel(type),
      status: 'empty' as LtcsRequestBtnStatus,
    }));
  }
}

export async function fetchSchedulePlanBtnStatus(
  memberCode: string,
  bizGubun: string,
  yearMonth: string
): Promise<LtcsRequestBtnStatus> {
  const items = await fetchLtcsTypeStatuses(
    memberCode,
    bizGubun,
    yearMonth,
    HR_LTCS_REQUEST_TYPES
  );
  return aggregateLtcsBtnStatus(items);
}

/** @deprecated fetchLtcsTypeStatuses 사용 권장 */
export async function fetchWageLtcsBtnStatus(
  memberCode: string,
  bizGubun: string,
  yearMonth: string
): Promise<LtcsRequestBtnStatus> {
  const items = await fetchLtcsTypeStatuses(
    memberCode,
    bizGubun,
    yearMonth,
    WAGE_LTCS_REQUEST_TYPES
  );
  return aggregateLtcsBtnStatus(items);
}
